import { geocodeAddress, haversineDistance } from './geocoding';

// Given the driver's current position and a list of selected loads,
// returns an ORDERED array of stops:
//   1. All pickups first (ONLY for loads still 'awaiting' pickup),
//      ordered nearest -> farthest using nearest-neighbor from current position
//   2. All dropoffs next (for EVERY selected load, regardless of status),
//      ordered nearest -> farthest starting from wherever the last pickup left off
//
// A load already marked 'picked up' (e.g. updated manually via Load Detail,
// without ever using the Map tab) correctly skips straight to needing only
// its dropoff — its pickup has already happened in real life.
//
// Each returned stop looks like:
// { type: 'pickup' | 'dropoff', loadId, address, lat, lng }
export async function buildOrderedRoute(currentPosition, selectedLoads) {
  // Geocode every pickup and dropoff address once, up front.
  // We geocode BOTH addresses regardless of status, since we may need
  // the dropoff either way — the status filtering happens below, only
  // for whether we include the PICKUP stop.
  const geocodedLoads = await Promise.all(
    selectedLoads.map(async (load) => {
      const pickupCoords = await geocodeAddress(load.origin);
      const dropoffCoords = await geocodeAddress(load.destination);
      return { load, pickupCoords, dropoffCoords };
    })
  );

  // --- Leg 1: pickups, only for loads still awaiting pickup ---
  const pickupStops = geocodedLoads
    .filter((g) => g.pickupCoords && g.load.load_status === 'awaiting')
    .map((g) => ({
      type: 'pickup',
      loadId: g.load.id_load,
      address: g.load.origin,
      lat: g.pickupCoords.lat,
      lng: g.pickupCoords.lng,
    }));

  const orderedPickups = nearestNeighborOrder(currentPosition, pickupStops);

  // --- Leg 2: dropoffs, for every selected load, starting from wherever
  //     the last pickup left off (or from current position if there were
  //     no pickups needed at all, e.g. every selected load was already picked up) ---
  const lastPickupPosition =
    orderedPickups.length > 0
      ? orderedPickups[orderedPickups.length - 1]
      : currentPosition;

  const dropoffStops = geocodedLoads
    .filter((g) => g.dropoffCoords)
    .map((g) => ({
      type: 'dropoff',
      loadId: g.load.id_load,
      address: g.load.destination,
      lat: g.dropoffCoords.lat,
      lng: g.dropoffCoords.lng,
    }));

  const orderedDropoffs = nearestNeighborOrder(lastPickupPosition, dropoffStops);

  return [...orderedPickups, ...orderedDropoffs];
}

// Simple nearest-neighbor ordering: repeatedly pick whichever remaining
// stop is closest to the current reference point, then move the
// reference point to that stop, and repeat.
function nearestNeighborOrder(startPosition, stops) {
  const remaining = [...stops];
  const ordered = [];
  let currentPos = startPosition;

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = haversineDistance(currentPos, remaining[0]);

    for (let i = 1; i < remaining.length; i++) {
      const dist = haversineDistance(currentPos, remaining[i]);
      if (dist < nearestDistance) {
        nearestDistance = dist;
        nearestIndex = i;
      }
    }

    const nextStop = remaining.splice(nearestIndex, 1)[0];
    ordered.push(nextStop);
    currentPos = nextStop;
  }

  return ordered;
}